import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useChat } from '../contexts/ChatContext';
import { FREE_MODE } from '../config/appConfig';
import { Button } from '../components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Home, Building2, MessageSquare, User, LogOut, Menu, Plus, Search, Shield, CreditCard, Package, Briefcase } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Navbar = () => {
  const { user, isAuthenticated, logout, token } = useAuth();
  const { unreadCount, fetchUnreadCount } = useChat();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isCompany = user?.user_type === 'company';
  const isAdmin = user?.is_admin;

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Mark all messages as read when clicking on messages link
  const handleMessagesClick = async () => {
    if (unreadCount > 0 && token) {
      try {
        await fetch(`${API}/messages/mark-all-read`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include',
        });
        fetchUnreadCount();
      } catch (error) {
        console.error('Error marking messages as read:', error);
      }
    }
  };

  return (
    <nav className="bg-slate-900 text-white sticky top-0 z-50" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-10 h-10 bg-orange-500 rounded-md flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div className="hidden sm:block">
              <span className="font-heading font-bold text-lg tracking-tight">Giełda Budowlana</span>
            </div>
          </Link>

          <div className="hidden md:flex items-center gap-4">
            <Link to="/offers" className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors" data-testid="nav-offers">
              <Search className="w-4 h-4" /><span className="font-body text-sm whitespace-nowrap">Giełda Ogłoszeń</span>
            </Link>
            <Link to="/materials" className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors" data-testid="nav-materials">
              <Package className="w-4 h-4 text-emerald-400" /><span className="font-body text-sm whitespace-nowrap">Giełda Materiałów</span>
            </Link>
            <Link to="/jobs" className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors" data-testid="nav-jobs">
              <Briefcase className="w-4 h-4 text-violet-400" /><span className="font-body text-sm whitespace-nowrap">Giełda Pracy</span>
            </Link>
            <Link to="/companies" className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors" data-testid="nav-companies">
              <Building2 className="w-4 h-4" /><span className="font-body text-sm">Firmy</span>
            </Link>

            {isAuthenticated ? (
              <>
                {isCompany ? (
                  <Link to="/dashboard/company" className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors" data-testid="nav-dashboard">
                    <Home className="w-4 h-4" /><span className="font-body text-sm">Panel firmy</span>
                  </Link>
                ) : (
                  <>
                    <Link to="/dashboard/individual" className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors" data-testid="nav-dashboard">
                      <Home className="w-4 h-4" /><span className="font-body text-sm">Mój panel</span>
                    </Link>
                    <Button onClick={() => navigate('/offer-choice')} className="bg-orange-500 hover:bg-orange-600 text-white font-medium text-sm" data-testid="add-offer-btn">
                      <Plus className="w-4 h-4 mr-1" />Dodaj ogłoszenie
                    </Button>
                  </>
                )}

                <Link 
                  to="/messages" 
                  onClick={handleMessagesClick}
                  className="relative flex items-center gap-2 text-slate-300 hover:text-white transition-colors" 
                  data-testid="nav-messages"
                >
                  <MessageSquare className="w-4 h-4" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center" data-testid="unread-badge">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Link>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2 text-slate-300 hover:text-white hover:bg-slate-800" data-testid="user-menu-trigger">
                      <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center">
                        {user?.picture ? <img src={user.picture} alt="" className="w-8 h-8 rounded-full" /> : <User className="w-4 h-4" />}
                      </div>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-2 py-1.5">
                      <p className="font-medium text-sm">{user?.name}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                    </div>
                    <DropdownMenuSeparator />
                    {isCompany && (
                      <>
                        <DropdownMenuItem onClick={() => navigate('/company/profile')} data-testid="menu-profile">
                          <Building2 className="w-4 h-4 mr-2" />Profil firmy
                        </DropdownMenuItem>
                        {!FREE_MODE && (
                          <DropdownMenuItem onClick={() => navigate('/subscription')} data-testid="menu-subscription">
                            <CreditCard className="w-4 h-4 mr-2" />Subskrypcja
                          </DropdownMenuItem>
                        )}
                      </>
                    )}
                    {!isCompany && (
                      <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="menu-profile">
                        <User className="w-4 h-4 mr-2" />Mój profil
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => { handleMessagesClick(); navigate('/messages'); }} data-testid="menu-messages" className="flex items-center justify-between">
                      <span className="flex items-center">
                        <MessageSquare className="w-4 h-4 mr-2" />Wiadomości
                      </span>
                      {unreadCount > 0 && (
                        <span className="bg-orange-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                      )}
                    </DropdownMenuItem>
                    {isAdmin && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                          <Shield className="w-4 h-4 mr-2" />Panel admina
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                      <LogOut className="w-4 h-4 mr-2" />Wyloguj się
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center gap-3">
                <Button variant="ghost" onClick={() => navigate('/login')} className="text-slate-300 hover:text-white hover:bg-slate-800" data-testid="login-btn">
                  Zaloguj się
                </Button>
                <Button onClick={() => navigate('/register')} className="bg-orange-500 hover:bg-orange-600 text-white font-medium" data-testid="register-btn">
                  Zarejestruj się
                </Button>
              </div>
            )}
          </div>

          <Button variant="ghost" className="md:hidden text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn">
            <Menu className="w-6 h-6" />
          </Button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-800">
            <div className="flex flex-col gap-2">
              <Link to="/offers" className="px-3 py-2 text-slate-300 hover:text-white hover:bg-slate-800 rounded-md flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                <Search className="w-4 h-4" />Giełda Ogłoszeń
              </Link>
              <Link to="/materials" className="px-3 py-2 text-slate-300 hover:text-white hover:bg-slate-800 rounded-md flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                <Package className="w-4 h-4 text-emerald-400" />Giełda Materiałów
              </Link>
              <Link to="/jobs" className="px-3 py-2 text-slate-300 hover:text-white hover:bg-slate-800 rounded-md flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                <Briefcase className="w-4 h-4 text-violet-400" />Giełda Pracy
              </Link>
              <Link to="/companies" className="px-3 py-2 text-slate-300 hover:text-white hover:bg-slate-800 rounded-md" onClick={() => setMobileMenuOpen(false)}>Firmy</Link>
              {isAuthenticated ? (
                <>
                  <Link to={isCompany ? '/dashboard/company' : '/dashboard/individual'} className="px-3 py-2 text-slate-300 hover:text-white hover:bg-slate-800 rounded-md" onClick={() => setMobileMenuOpen(false)}>
                    {isCompany ? 'Panel firmy' : 'Mój panel'}
                  </Link>
                  <Link to="/messages" className="px-3 py-2 text-slate-300 hover:text-white hover:bg-slate-800 rounded-md flex items-center justify-between" onClick={() => { handleMessagesClick(); setMobileMenuOpen(false); }}>
                    <span>Wiadomości</span>
                    {unreadCount > 0 && (
                      <span className="bg-orange-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </Link>
                  {isAdmin && <Link to="/admin" className="px-3 py-2 text-slate-300 hover:text-white hover:bg-slate-800 rounded-md" onClick={() => setMobileMenuOpen(false)}>Panel admina</Link>}
                  <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="px-3 py-2 text-red-400 hover:text-red-300 hover:bg-slate-800 rounded-md text-left">Wyloguj się</button>
                </>
              ) : (
                <>
                  <Link to="/login" className="px-3 py-2 text-slate-300 hover:text-white hover:bg-slate-800 rounded-md" onClick={() => setMobileMenuOpen(false)}>Zaloguj się</Link>
                  <Link to="/register" className="px-3 py-2 bg-orange-500 text-white rounded-md text-center" onClick={() => setMobileMenuOpen(false)}>Zarejestruj się</Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
